import { useEffect, useMemo } from "react";
import { IFramePopupData, PopupType } from "../types/popup";
import { ActionButtonData, PageType } from "../types/site";
import {
  ButtonContext,
  MESSAGE_TYPE_BUTTON_CLICK,
  MESSAGE_TYPE_BUTTON_DATA,
  MESSAGE_TYPE_INVALIDATE_BUTTONS,
  MESSAGE_TYPE_OPEN_PAGE,
  MESSAGE_TYPE_PLUGIN_INIT,
  MESSAGE_TYPE_HANDLING_COMPLETE,
  MESSAGE_TYPE_SET_PROVIDER_DATA,
  MESSAGE_TYPE_SET_FINAL_PRICE,
  MESSAGE_TYPE_SHOW_BUTTON,
  MESSAGE_TYPE_SHOW_POPUP,
  MESSAGE_TYPE_UPDATE_BUTTON,
  PageContext,
  MESSAGE_TYPE_INIT_COMPLETE,
  MESSAGE_TYPE_SET_POPUP_RESULT,
  MESSAGE_TYPE_POPUP_CLOSED,
  MESSAGE_TYPE_ANALYTICS,
  MESSAGE_TYPE_CONTEXT_CHANGED,
} from "../utils/extentionContants";
import { CLOSE_POPUP_EVENT, SET_POPUP_RESULT_EVENT } from "../utils/popups";
import { SALES_LEAD_DATE_CHANGED_MESSAGE, loadLeadDates, sendAnalyticsData } from "../utils/analitics";
import { showPopup } from "./usePopups";
import { WebWorkerActionBututton } from "../types/action-buttons";

type ActionButtonListener = (message: any) => void;
class ButtonContextData {
  listener?: ActionButtonListener;
  messageSent = false;
  delayedMessages: any[] = [];

  constructor(listener: ActionButtonListener) {
    this.listener = listener;
  }
}

export class WorkerWrapper {
  private contextsMap: Map<string, ButtonContextData> = new Map<string, ButtonContextData>();
  private _contextData: PageContext;
  private initialized: boolean = false;
  private worker: Worker;
  private workerUrl: string;

  public get contextData(): PageContext {
    return this._contextData;
  }

  constructor(
    workerData: string,
    private readonly _showPopup: any,
    private readonly providerName: string,
    contextData: PageContext,
  ) {
    const buffer = Buffer.from(workerData, "base64");
    const blob = new Blob([buffer], { type: "application/javascript" });
    this.workerUrl = URL.createObjectURL(blob);
    this.worker = new Worker(this.workerUrl);
    this._contextData = contextData;
    this.worker.addEventListener("message", (event: MessageEvent) => {
      // console.log("host message", event.data.type);
      switch (event.data.type) {
        case MESSAGE_TYPE_INIT_COMPLETE: {
          this.onInitialized();
          break;
        }
        case MESSAGE_TYPE_SET_FINAL_PRICE:
        case MESSAGE_TYPE_SHOW_BUTTON:
        case MESSAGE_TYPE_UPDATE_BUTTON: {
          this.processContextMessage(event.data);
          break;
        }
        case MESSAGE_TYPE_BUTTON_DATA: {
          this.processContextMessage(event.data);
          this.onMessageComplete(event.data.contextId);
          break;
        }
        case MESSAGE_TYPE_HANDLING_COMPLETE: {
          this.onMessageComplete(event.data.contextId);
          break;
        }
        case MESSAGE_TYPE_SHOW_POPUP: {
          const popupData: IFramePopupData = {
            url: event.data.popup.url,
            width: event.data.popup.width,
            height: event.data.popup.height,
            mobileWidth: event.data.popup.mobileWidth,
            mobileHeight: event.data.popup.mobileHeight,
            allowScrolling: event.data.popup.allowScrolling,
            allowFullScreen: event.data.popup.allowFullScreen,
            closeButtonType: event.data.popup.closeButtonType,
            maxWidth: event.data.popup.maxWidth,
            maxHeight: event.data.popup.maxHeight,
          };
          const providerData = event.data.popupData;
          popupData.onMessageCallback = (data: any) => {
            if (data.type === SET_POPUP_RESULT_EVENT) {
              this.worker.postMessage({
                type: MESSAGE_TYPE_SET_POPUP_RESULT,
                context: this.contextData,
                popupData: providerData,
                popupResult: data.data,
              });
            } else if (data.type === CLOSE_POPUP_EVENT) {
              this.worker.postMessage({
                type: MESSAGE_TYPE_POPUP_CLOSED,
                context: this.contextData,
                popupData: providerData,
              });
            }
          };
          this._showPopup(PopupType.IFRAME, popupData);
          break;
        }
        case MESSAGE_TYPE_OPEN_PAGE: {
          const url = event.data.url;
          Object.assign(document.createElement("a"), {
            target: "_blank",
            rel: "noopener noreferrer",
            href: url,
          }).click();
          break;
        }
        case MESSAGE_TYPE_SET_PROVIDER_DATA: {
          const data = event.data.data;
          if (data) {
            const newProviderData = { ...this._contextData.providerData, ...data };
            this._contextData.providerData = newProviderData;
            window.localStorage.setItem(this.providerName, JSON.stringify(this._contextData.providerData));
          } else {
            this._contextData.providerData = undefined;
            window.localStorage.removeItem(this.providerName);
          }
          break;
        }
        case MESSAGE_TYPE_INVALIDATE_BUTTONS: {
          if (!event.data.exceptButtons || !Array.isArray(event.data.exceptButtons)) {
            const listeners = Array.from(this.contextsMap.values());
            listeners.forEach((item) => {
              if (item.listener) item.listener({ type: MESSAGE_TYPE_UPDATE_BUTTON });
            });
          } else {
            const contextIds = Array.from(this.contextsMap.keys());
            const filteredContextIds = contextIds.filter((item) => !event.data.exceptButtons.includes(item));
            filteredContextIds.forEach((contextId) => {
              const contextButton = this.contextsMap.get(contextId);
              if (contextButton?.listener) {
                contextButton.listener({ type: MESSAGE_TYPE_UPDATE_BUTTON });
              }
            });
          }
          break;
        }
        case MESSAGE_TYPE_ANALYTICS: {
          sendAnalyticsData(event.data.data);
          break;
        }
      }
    });

    window.addEventListener(SALES_LEAD_DATE_CHANGED_MESSAGE, (event: any) => {
      this._contextData.salesLeadDate = event.detail;
      this.contextChanged();
    });
  }

  private processContextMessage(messageData: any) {
    const contextId = messageData.contextId;
    const contextButton = this.contextsMap.get(contextId);
    if (contextButton?.listener) {
      contextButton.listener(messageData);
    }
  }

  private onInitialized() {
    this.initialized = true;
    const contextIds = Array.from(this.contextsMap.keys());
    contextIds.forEach((contextId) => {
      this.onMessageComplete(contextId);
    });
  }

  private onMessageComplete(contextId: string) {
    const contextButton = this.contextsMap.get(contextId);
    if (contextButton) {
      if (contextButton.delayedMessages.length) {
        const nextMessage = contextButton.delayedMessages[0];
        contextButton.delayedMessages.splice(0, 1);
        this.worker.postMessage(nextMessage);
      } else {
        contextButton.messageSent = false;
      }
    }
  }

  init() {
    this.worker.postMessage({ type: MESSAGE_TYPE_PLUGIN_INIT, context: this.contextData });
  }

  contextChanged() {
    this.worker.postMessage({ type: MESSAGE_TYPE_CONTEXT_CHANGED, context: this.contextData });
  }

  postMessage(data: any) {
    if (data.context) {
      const contextId = data.context.contextId;
      const contextButton = this.contextsMap.get(contextId);
      if (contextButton) {
        if (this.initialized && !contextButton.messageSent) {
          contextButton.messageSent = true;
          this.worker.postMessage(data);
        } else {
          contextButton.delayedMessages.push(data);
        }
      }
    }
  }

  terminate() {
    this.worker.terminate();
    URL.revokeObjectURL(this.workerUrl);
  }

  setListener(name: string, listener: ActionButtonListener) {
    const contextButton = this.contextsMap.get(name);
    const newContextButton = new ButtonContextData(listener);
    if (contextButton) {
      newContextButton.delayedMessages = contextButton.delayedMessages;
      newContextButton.messageSent = contextButton.messageSent;
    }
    this.contextsMap.set(name, newContextButton);
  }

  deleteListener(name: string) {
    const listener = this.contextsMap.get(name);
    if (listener) {
      if (!listener.messageSent && listener.delayedMessages.length === 0) {
        this.contextsMap.delete(name);
      } else {
        listener.listener = undefined;
      }
    }
  }

  postClickMessage(context: ButtonContext) {
    this.postMessage({ type: MESSAGE_TYPE_BUTTON_CLICK, context });
  }
}

export const useWebWorkerActionButtons = (
  actionButtons: ActionButtonData[],
  pageType: PageType,
  pageName: string,
  host: string,
  language?: string,
): WebWorkerActionBututton[] => {
  const resultButtons: WebWorkerActionBututton[] = useMemo(() => {
    if (typeof Worker !== "undefined") {
      return actionButtons.map((item) => {
        const result: WebWorkerActionBututton = { ...item };
        if (item.dynamic && item.scriptData) {
          const context: PageContext = {
            pageType,
            pageName,
            host,
            language,
            actionButtonData: item.actionButtonData,
            screenWidth: window.innerWidth,
            screenHeight: window.innerHeight,
            ...loadLeadDates(),
          };
          const providerName = `provider_${item.id}`;
          const providerData = window.localStorage.getItem(providerName);
          context.providerData = providerData ? JSON.parse(providerData) : undefined;
          result.worker = new WorkerWrapper(item.scriptData, showPopup, providerName, context);
          result.worker.init();
        }
        return result;
      });
    } else {
      return actionButtons;
    }
  }, [actionButtons, host, language, pageName, pageType]);

  useEffect(() => {
    return () => {
      resultButtons.forEach((button) => {
        if (button.worker) {
          button.worker.terminate();
          button.worker = undefined;
        }
      });
    };
  }, [resultButtons]);

  return resultButtons;
};
